import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Settings as SettingsIcon, MessageSquare, Tag, Receipt, Layers, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useSubcategories } from '../hooks/useSubcategories';
import ImageUploader from '../components/ImageUploader';
import ProductImageUploader from '../components/ProductImageUploader';
import ProductCustomization from '../components/ProductCustomization';
import Model3DUploader from '../components/Model3DUploader';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { grouped: subcatByParent } = useSubcategories();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    price: '',
    category: 'residencial',
    type: '',
    images: [''],
    stock: '',
    featured: false,
    is_customizable: false,
    fabric_options: [],
    size_options: [],
    material_options: [],
    free_dimensions_enabled: false,
    free_dimensions_mode: 'm2',
    price_per_linear_meter: 0,
  });

  useEffect(() => {
    if (authLoading) return; // Wait for auth to load
    
    if (!user || !user.is_admin) {
      toast.error('Acesso negado. Apenas administradores.');
      navigate('/');
      return;
    }
    fetchProducts();
  }, [authLoading, user, navigate]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.type) {
      toast.error('Selecione uma subcategoria antes de salvar');
      return;
    }

    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock),
      images: formData.images.filter((img) => img.trim() !== ''),
      price_per_linear_meter: parseFloat(formData.price_per_linear_meter) || 0,
    };

    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.id}`, productData);
        toast.success('Produto atualizado com sucesso!');
      } else {
        await axios.post(`${API_URL}/products`, productData);
        toast.success('Produto criado com sucesso!');
      }
      
      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail && typeof error.response.data.detail === 'string' ? error.response.data.detail : 'Erro ao salvar produto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      sku: product.sku || '',
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      type: product.type,
      images: product.images.length > 0 ? product.images : [''],
      stock: product.stock.toString(),
      featured: product.featured,
      is_customizable: product.is_customizable || false,
      fabric_options: product.fabric_options || [],
      size_options: product.size_options || [],
      material_options: product.material_options || [],
      free_dimensions_enabled: product.free_dimensions_enabled || false,
      free_dimensions_mode: product.free_dimensions_mode || 'm2',
      price_per_linear_meter: product.price_per_linear_meter || 0,
      model_3d_url: product.model_3d_url || '',
      model_3d_format: product.model_3d_format || '',
      source_file_url: product.source_file_url || '',
      source_file_format: product.source_file_format || '',
      dimensions_display: product.dimensions_display || '',
      materials_display: product.materials_display || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;

    try {
      await axios.delete(`${API_URL}/products/${productId}`);
      toast.success('Produto excluído com sucesso!');
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
    sku: '',
      description: '',
      price: '',
      category: 'residencial',
      type: '',
      images: [],
      stock: '',
      featured: false,
      is_customizable: false,
      fabric_options: [],
      size_options: [],
      material_options: [],
      free_dimensions_enabled: false,
      free_dimensions_mode: 'm2',
      price_per_linear_meter: 0,
      model_3d_url: '',
      model_3d_format: '',
      source_file_url: '',
      source_file_format: '',
      dimensions_display: '',
      materials_display: '',
    });
  };

  if (authLoading) {
    return (
      <div data-testid="loading-state" className="py-20 text-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (!user || !user.is_admin) return null;

  return (
    <div data-testid="admin-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between mb-16">
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
            Painel Administrativo
          </h1>
          <div className="flex items-center gap-4">
            <Link
              to="/admin/promocoes"
              data-testid="promotions-admin-link"
              className="flex items-center gap-2 bg-secondary text-primary px-6 py-3 uppercase tracking-widest text-xs hover:bg-primary hover:text-white border border-primary transition-all duration-500"
            >
              <Tag className="w-4 h-4" strokeWidth={1.5} />
              Promoções
            </Link>
            <Link
              to="/admin/orcamentos"
              data-testid="quotes-link"
              className="flex items-center gap-2 bg-secondary text-primary px-6 py-3 uppercase tracking-widest text-xs hover:bg-primary hover:text-white border border-primary transition-all duration-500"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
              Orçamentos
            </Link>
            <Link
              to="/admin/pedidos"
              data-testid="orders-link"
              className="flex items-center gap-2 bg-secondary text-primary px-6 py-3 uppercase tracking-widest text-xs hover:bg-primary hover:text-white border border-primary transition-all duration-500"
            >
              <Receipt className="w-4 h-4" strokeWidth={1.5} />
              Pedidos
            </Link>
            <Link
              to="/admin/categorias"
              data-testid="categories-link"
              className="flex items-center gap-2 bg-secondary text-primary px-6 py-3 uppercase tracking-widest text-xs hover:bg-primary hover:text-white border border-primary transition-all duration-500"
            >
              <Layers className="w-4 h-4" strokeWidth={1.5} />
              Categorias
            </Link>
            <Link
              to="/admin/metricas-3d"
              data-testid="metrics-3d-link"
              className="flex items-center gap-2 bg-secondary text-primary px-6 py-3 uppercase tracking-widest text-xs hover:bg-primary hover:text-white border border-primary transition-all duration-500"
            >
              <BarChart3 className="w-4 h-4" strokeWidth={1.5} />
              Métricas 3D
            </Link>
            <Link
              to="/admin/configuracoes"
              data-testid="settings-link"
              className="flex items-center gap-2 bg-secondary text-primary px-6 py-3 uppercase tracking-widest text-xs hover:bg-primary hover:text-white border border-primary transition-all duration-500"
            >
              <SettingsIcon className="w-4 h-4" strokeWidth={1.5} />
              Configurações
            </Link>
            <button
              onClick={() => {
                setShowForm(true);
                setEditingProduct(null);
                resetForm();
              }}
              data-testid="add-product-button"
              className="flex items-center gap-2 bg-primary text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-black transition-all duration-500"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Adicionar Produto
            </button>
          </div>
        </div>

        {/* Product Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
            <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-3xl font-light tracking-tight">
                  {editingProduct ? 'Editar Produto' : 'Novo Produto'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                  data-testid="close-form"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} data-testid="product-form" className="space-y-6">
                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Nome do Produto
                  </label>
                  <input
                    type="text"
                    data-testid="product-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                  />
                </div>

                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Descrição
                  </label>
                  <textarea
                    data-testid="product-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={4}
                    className="w-full bg-transparent border border-border focus:border-primary p-3 focus:ring-0 outline-none transition-colors duration-300 resize-none"
                  />
                </div>

                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    SKU / Código (usado na importação de promoções)
                  </label>
                  <input
                    type="text"
                    data-testid="product-sku-input"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    placeholder="Ex: SOF-001"
                    className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Preço (R$)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="product-price-input"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                    />
                  </div>

                  <div>
                    <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Estoque
                    </label>
                    <input
                      type="number"
                      data-testid="product-stock-input"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Categoria
                    </label>
                    <select
                      data-testid="product-category-input"
                      value={formData.category}
                      onChange={(e) => {
                        setFormData({ ...formData, category: e.target.value, type: '' });
                      }}
                      className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                    >
                      <option value="residencial">Residencial</option>
                      <option value="corporativo">Corporativo</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Subcategoria
                    </label>
                    <select
                      data-testid="product-type-input"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                    >
                      <option value="">Selecione...</option>
                      {subcatByParent[formData.category]?.map((sub) => (
                        <option key={sub.id} value={sub.slug}>
                          {sub.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <ProductImageUploader
                  existingImages={formData.images}
                  onUploadComplete={(urls) => setFormData({ ...formData, images: urls })}
                />

                <ProductCustomization
                  productData={formData}
                  onUpdate={(field, value) => setFormData({ ...formData, [field]: value })}
                />

                <Model3DUploader
                  modelUrl={formData.model_3d_url}
                  modelFormat={formData.model_3d_format}
                  sourceFileUrl={formData.source_file_url}
                  sourceFileFormat={formData.source_file_format}
                  dimensionsDisplay={formData.dimensions_display}
                  materialsDisplay={formData.materials_display}
                  onChange={(url, format) => setFormData({ ...formData, model_3d_url: url, model_3d_format: format })}
                  onSourceChange={(url, format) => setFormData({ ...formData, source_file_url: url, source_file_format: format })}
                  onFieldChange={(field, value) => setFormData({ ...formData, [field]: value })}
                />

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="featured"
                    data-testid="product-featured-input"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="featured" className="text-sm text-muted-foreground">
                    Produto em destaque
                  </label>
                </div>

                <div className="flex gap-4 pt-6">
                  <button
                    type="submit"
                    data-testid="save-product-button"
                    className="flex-1 bg-primary text-white px-8 py-4 uppercase tracking-widest text-xs hover:bg-black transition-all duration-500"
                  >
                    {editingProduct ? 'Atualizar' : 'Criar'} Produto
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingProduct(null);
                      resetForm();
                    }}
                    className="flex-1 bg-transparent border border-primary text-primary px-8 py-4 uppercase tracking-widest text-xs hover:bg-primary hover:text-white transition-all duration-500"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Products List */}
        {loading ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Carregando produtos...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Nenhum produto cadastrado ainda.</p>
          </div>
        ) : (
          <div className="border border-border">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr>
                  <th className="text-left p-4 font-accent text-xs uppercase tracking-widest">Produto</th>
                  <th className="text-left p-4 font-accent text-xs uppercase tracking-widest">Categoria</th>
                  <th className="text-left p-4 font-accent text-xs uppercase tracking-widest">Preço</th>
                  <th className="text-left p-4 font-accent text-xs uppercase tracking-widest">Estoque</th>
                  <th className="text-right p-4 font-accent text-xs uppercase tracking-widest">Ações</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} data-testid={`admin-product-row-${product.id}`} className="border-b border-border">
                    <td className="p-4">
                      <div className="flex items-center gap-4">
                        <img
                          src={product.images[0] || '/placeholder.svg'}
                          alt={product.name}
                          className="w-16 h-20 object-cover"
                        />
                        <div>
                          <span className="font-normal">{product.name}</span>
                          {product.sku && <p data-testid={`admin-product-sku-${product.id}`} className="text-xs text-muted-foreground">SKU {product.sku}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">{product.category}</td>
                    <td className="p-4 font-medium">R$ {product.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    <td className="p-4 text-sm">{product.stock}</td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => handleEdit(product)}
                          data-testid={`edit-product-${product.id}`}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          <Edit className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          data-testid={`delete-product-${product.id}`}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;